import type { EvaluationResult, TestSetWithDetails } from '@shared/types/evaluation'

export interface ExportData {
  testSet: TestSetWithDetails
  results: EvaluationResult[]
  exportDate: string
}

export function exportToCSV(data: ExportData): void {
  const { testSet, results, exportDate } = data
  
  // Create CSV headers
  const headers = [
    'Test Case #',
    'Question',
    'Expected Answer',
    'RAG Response',
    'Score',
    'Passed',
    'Latency (ms)',
    'Judge Reasoning',
    'Confidence'
  ]
  
  // Create CSV rows
  const rows = results.map((result, index) => [
    (index + 1).toString(),
    `"${(result.question || '').replace(/"/g, '""')}"`,
    `"${(result.goldenAnswer || '').replace(/"/g, '""')}"`,
    `"${(result.ragResponse || '').replace(/"/g, '""')}"`,
    (result.judgeScore || 0).toFixed(2),
    result.passed ? 'PASSED' : 'FAILED',
    result.latencyMs?.toString() || '0',
    `"${(result.reasoning || '').replace(/"/g, '""')}"`,
    result.confidence?.toFixed(2) || ''
  ])
  
  // Combine headers and rows
  const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
  
  // Add BOM for proper UTF-8 encoding
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
  
  // Create download link
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `evaluation-results-${testSet.name}-${exportDate}.csv`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function exportToJSON(data: ExportData): void {
  const { testSet, results, exportDate } = data
  
  const exportData = {
    metadata: {
      testSetName: testSet.name,
      testSetId: testSet.id,
      description: testSet.description,
      systemPrompt: testSet.systemPrompt,
      model: testSet.model,
      temperature: testSet.temperature,
      totalTestCases: testSet._count.testCases,
      totalEvaluationRuns: testSet._count.evaluationRuns,
      exportDate,
      exportedBy: 'System Admin' // Could be dynamic based on current user
    },
    latestRun: testSet.latestRun ? {
      id: testSet.latestRun.id,
      status: testSet.latestRun.status,
      averageScore: testSet.latestRun.averageScore,
      startedAt: testSet.latestRun.startedAt
    } : null,
    results: results.map((result, index) => ({
      testCaseNumber: index + 1,
      id: result.id,
      runId: result.runId,
      testCaseId: result.testCaseId,
      question: result.question,
      goldenAnswer: result.goldenAnswer,
      ragResponse: result.ragResponse,
      score: result.judgeScore || 0,
      passed: result.passed,
      reasoning: result.reasoning,
      latencyMs: result.latencyMs,
      confidence: result.confidence
    }))
  }
  
  const jsonString = JSON.stringify(exportData, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json' })
  
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `evaluation-results-${testSet.name}-${exportDate}.json`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function exportToMarkdown(data: ExportData): void {
  const { testSet, results, exportDate } = data
  
  const markdown = `# Evaluation Results: ${testSet.name}

## Test Set Information
- **Test Set ID**: ${testSet.id}
- **Description**: ${testSet.description || 'No description'}
- **Model**: ${testSet.model}
- **Temperature**: ${testSet.temperature}
- **Total Test Cases**: ${testSet._count.testCases}
- **Total Evaluation Runs**: ${testSet._count.evaluationRuns}
- **Export Date**: ${exportDate}

## System Prompt
\`\`\`
${testSet.systemPrompt}
\`\`\`

## Latest Run Summary
${testSet.latestRun ? `
- **Status**: ${testSet.latestRun.status}
- **Average Score**: ${testSet.latestRun.averageScore?.toFixed(2) || 'N/A'}
- **Started At**: ${new Date(testSet.latestRun.startedAt).toLocaleString()}
` : 'No evaluation runs yet.'}

## Test Case Results

${results.map((result, index) => `
### Test Case #${index + 1}
- **Score**: ${(result.judgeScore || 0).toFixed(2)}
- **Status**: ${result.passed ? '✅ PASSED' : '❌ FAILED'}
- **Latency**: ${result.latencyMs}ms
${result.confidence ? `- **Confidence**: ${result.confidence.toFixed(2)}` : ''}

**Question:**
> ${result.question}

**Expected Answer:**
> ${result.goldenAnswer}

**RAG Response:**
> ${result.ragResponse}

${result.reasoning ? `**Judge Reasoning:**
> ${result.reasoning}` : ''}

---
`).join('\n')}

## Summary Statistics
- **Total Test Cases**: ${results.length}
- **Passed**: ${results.filter(r => r.passed).length}
- **Failed**: ${results.filter(r => !r.passed).length}
- **Pass Rate**: ${results.length > 0 ? ((results.filter(r => r.passed).length / results.length) * 100).toFixed(1) : 0}%
- **Average Score**: ${results.length > 0 ? (results.reduce((sum, r) => sum + (r.judgeScore || 0), 0) / results.length).toFixed(2) : 'N/A'}
- **Average Latency**: ${results.length > 0 ? (results.reduce((sum, r) => sum + (r.latencyMs || 0), 0) / results.length).toFixed(0) : 0}ms
`
  
  const blob = new Blob([markdown], { type: 'text/markdown' })
  
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `evaluation-results-${testSet.name}-${exportDate}.md`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}